import dayjs from "dayjs";
import { VALID_TIME_FORMATS, ARMORED_CAR_DEFAULT_VALUES } from "../constants";
const customParseFormat = require("dayjs/plugin/customParseFormat");

/**
 * Validates the entries in a form for every key given in inputValidationTypes, given the form as an object
 * @param {*} form
 * @param {Object} inputValidationTypes - Object containing the keys to validate and the 'types
 * @returns
 */
export function isInvalidForm(form: any, inputValidationTypes: any) {
  let isInvalid = false;
  let invalidFields = {};
  let isEmpty = true;
  for (const key in inputValidationTypes) {
    if (form.hasOwnProperty(key)) {
      if (form[key] !== null && form[key].length !== 0) {
        isEmpty = false;
        if (isInvalidInput(form[key], inputValidationTypes[key])) {
          isInvalid = true;
          // @ts-ignore
          invalidFields[key] = form[key];
        }
      }
    }
  }
  return { isInvalid, invalidFields, isEmpty };
}

export function isInvalidInput(text: any, type: any) {
  if (!text || text.length === 0) return false;
  switch (type) {
    case "digits":
      return !isDigits(text);
    case "percent":
      return !isValidPercentage(text);
    case "money":
      return !isMoney(text, false);
    case "money-optional-sign":
      return !isMoney(text, true);
    case "phone":
      return !isPhone(text);
    case "date":
      return isDatetimeFormatInvalid(text, VALID_TIME_FORMATS["iso-date"]);
    case "12hour-timestamp":
      return isDatetimeFormatInvalid(
        text,
        VALID_TIME_FORMATS["12hour-timestamp"]
      );
    case "weekdays":
      // In this case, "text" is an array
      return !hasOnlyWeekdays(text);
    default:
      return false;
  }
}

export function isDigits(text: string) {
  return /^\d+$/.test(text);
}

export function isValidPercentage(text: string) {
  return /(^100([.]0{1,2})?)$|(^\d{1,2}([.]\d{1,2})?)$/.test(text);
}

export function isMoney(text: string, canHaveDollarSign = true) {
  return canHaveDollarSign
    ? /^\$?\d+(,\d{3})*(\.\d{1,2})?$/.test(text)
    : /^\d+(\.\d{1,2})?$/.test(text);
}

export function isPhone(text: string) {
  return /^\d{3}-\d{3}-\d{4}$/.test(text);
}

/**
 * Ensure that only weekdays in titlecase (i.e. 'Monday' instead of 'monday') are in array
 * @param {String} array - Array containing weekdays
 * @returns {Boolean} - Indicates whether array contains only weekdays
 */
export function hasOnlyWeekdays(array: []) {
  // Check for null or non-unique values in array
  if (array == null || new Set(array).size !== array.length) {
    return false;
  }
  const weekdays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
    "N/A",
    "",
  ];
  return array.every((day: string) => weekdays.includes(day));
}

/**
 * Convert code to phase if applicable, if not, return entered code
 * @param {number} code - A negative number (-1, -2, or -3) representing 'OPR CHG', 'OSL', or 'DFL',
 *                                  or a different number that doesn't get converted
 *
 * @returns string phase mapped from number, or the input number converted to string
 */
export function convertCodeToPhase(code: number) {
  // Return blank string instead of null phase to display in UI
  if (code == null) {
    return "";
  }
  switch (code) {
    case -1:
      return "OPR CHG";
    case -2:
      return "OSL";
    case -3:
      return "DFL";
    default:
      // if no case matches, return phase unmodified
      return code.toString();
  }
}

/**
 * Convert a phase to code if applicable, if not, return entered phase
 * @param {number} phase - A phase string 'OPR CHG', 'OSL', or 'DFL' to be mapped to -1, -2, or -3
 *                         OR a custom code
 *
 * @returns number mapped from input string, or the unmodified input string
 */
export function convertPhaseToCode(phase: string) {
  // If phase to convert is null or blank, send to API as null
  if (phase == null || phase === "") {
    return null;
  }
  switch (phase) {
    case "OPR CHG":
      return -1;
    case "OSL":
      return -2;
    case "DFL":
      return -3;
    default:
      return parseInt(phase);
  }
}

/**
 * Validate that the input string doesn't contain anything other than 'OPR CHG', 'OSL', 'DFL', or a number
 * @param {String} phase - A phase that can either be one of 'OPR CHG', 'OSL', 'DFL', or a number
 *
 * @returns {boolean} - return true if phase is invalid, and false otherwise
 */
export function isPhaseInvalid(phase: string) {
  if (phase === "") {
    return false;
  }
  const trimmedPhase = phase.toUpperCase().trim();
  return !(
    ["OPR CHG", "OSL", "DFL"].includes(trimmedPhase) || isDigits(trimmedPhase)
  );
}

export function isPhaseValid(phase: string) {
  if (phase === "") {
    return false;
  }
  const trimmedPhase = phase.toUpperCase().trim();
  return (
    ["OPR CHG", "OSL", "DFL"].includes(trimmedPhase) || isDigits(trimmedPhase)
  );
}

/**
 * Validate that a datetime input is in the format specified
 * (i.e. for 12 hour format, text = '2:00 AM', format = 'h:mm a')
 * @param {String} text - The date string to validate
 * @param {String} format - The dayjs format of the string. Defaults to ISO timestamp format for dayjs library.
 * @returns {boolean} - A boolean indicating whether the date string is valid
 */
export function isDatetimeFormatInvalid(
  text: string,
  format = "YYYY-MM-DDTHH:mm:ss.SSSZZ"
) {
  if (text === "" || text == null) {
    return false;
  } else dayjs.extend(customParseFormat);
  return !dayjs(text.toUpperCase(), format, true).isValid();
}

export function isDatetimeFormatValid(
  text: string,
  format = "YYYY-MM-DDTHH:mm:ss.SSSZZ"
) {
  if (text === "" || text == null) {
    return false;
  } else dayjs.extend(customParseFormat);
  return dayjs(text.toUpperCase(), format, true).isValid();
}

export function isDateFormatValid(text: string, format = "YYYY-MM-DD") {
  if (text === "" || text == null) {
    return false;
  } else dayjs.extend(customParseFormat);
  return dayjs(text.toUpperCase(), format, true).isValid();
}

export function isTimeFormatValid(text: string, format = "HH:mm:ss") {
  if (text === "" || text == null) {
    return false;
  } else dayjs.extend(customParseFormat);
  return dayjs(text.toUpperCase(), format, true).isValid();
}

export function createOperatorName(firstName: string, lastName: string) {
  // If firstName and lastName are 'truthy', combine names
  return firstName && lastName
    ? firstName + " " + lastName
    : ARMORED_CAR_DEFAULT_VALUES.operatorEmptyName;
}

export function isLocationNumberInvalid(number: any) {
  if (number === "" || number === null) {
    return false;
  }
  return number.length !== 5;
}

export function isLocationNumberValid(number: any) {
  if (number === "" || number === null) {
    return false;
  }
  return number.length === 5;
}

export function isBusinessUnitNumberValid(number: any) {
  if (number === "" || number === null) {
    return false;
  }
  return number.length === 10;
}

export function isBusinessUnitNumberInvalid(number: any) {
  if (number === "" || number === null) {
    return false;
  }
  return number.length !== 10;
}
